import { apiFetch } from "./api";

export const MODULE_ID = "escala";

// Os três papéis de negócio que module_role conhece pra "escala" — strings
// opacas pra plataforma, sem API própria pra listar, então ficam hardcoded
// aqui (mesmo padrão do lado Go, que trata como consts, não tabela).
export const ESCALA_ROLES = ["Encarregado", "Gerente", "Administrador de Escala"] as const;

// resumo cabe numa linha de tabela sem quebrar; completo é o que a tela de
// vínculo de cada papel mostra por extenso (appdono/CLAUDE.md "Papéis").
export const ESCALA_PAPEL_DESCRICAO: Record<(typeof ESCALA_ROLES)[number], { resumo: string; completo: string }> = {
  "Encarregado": {
    resumo: "Cria e gera a escala da própria equipe",
    completo:
      "Cadastra a escala da loja, vincula a equipe e gera a grade de folgas dentro do prazo de confecção. Só enxerga e edita as escalas que ele mesmo criou.",
  },
  "Gerente": {
    resumo: "Faz tudo nas suas lojas, inclusive desaprovar",
    completo:
      "Vê e edita a escala de qualquer encarregado das lojas a que tem acesso, sem o filtro por autoria. Valida, aprova e desaprova a escala gerada, remove de vez a que foi cancelada, libera a edição fora do prazo, importa planilhas e acessa os relatórios de vale-transporte, folgas e domingos trabalhados. Pode editar a grade mesmo fora do prazo de confecção, quando o encarregado já não pode mais.",
  },
  "Administrador de Escala": {
    resumo: "Todas as lojas e a configuração da Escala",
    completo:
      "Acumula as permissões do Gerente em todas as lojas do client e é o único que mexe no que vale para o client inteiro: parâmetros, prazos de confecção, cargas horárias, feriados gerais, integração externa e permissões.",
  },
};

// Loja é um espelho somente-leitura da company da plataforma (sdk.Company
// no Go) — a Escala não cadastra loja própria nem edita seus dados; isso
// acontece só em /empresas (appdono/CLAUDE.md, "loja da Escala é a company
// da plataforma"). nome/razao_social vêm nulos quando a company não os tem
// preenchidos.
export interface Loja {
  id: string;
  cnpj: string;
  nome?: string;
  razao_social?: string;
  ativo: boolean;
}

// nome/razao_social podem vir em branco (cadastro é livre em /empresas),
// então cai pro cnpj em vez de mostrar rótulo vazio — usado por toda tela
// que lista ou seleciona loja.
export function lojaLabel(loja: Loja): string {
  return loja.nome || loja.razao_social || loja.cnpj;
}

// Nome de um item de lista por id ("" quando não está na lista) — escala, colaborador.
export function nomeDe<T extends { id?: string; nome: string }>(itens: T[], id?: string | null) {
  return itens.find((i) => i.id === id)?.nome ?? "";
}

// Rótulo da loja pelo id ("" quando não está na lista).
export function rotuloLoja(lojas: Loja[], id?: string | null) {
  const loja = lojas.find((l) => l.id === id);
  return loja ? lojaLabel(loja) : "";
}

// formatDataBR é o "AAAA-MM-DD" -> "DD/MM/AAAA" que toda tela do módulo com
// coluna de data precisa — estava duplicado em EscalaAusenciasTab,
// EscalaFeriadosTab e EscalaPrazosTab, cada um com a própria cópia.
export function formatDataBR(iso?: string) {
  if (!iso) return "";
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

// Data (no fuso do navegador) de um timestamp RFC3339 — cortar a string
// mostraria a data em UTC, que vira o dia seguinte à noite.
export function formatTimestampBR(timestamp?: string) {
  return timestamp ? new Date(timestamp).toLocaleDateString("pt-BR") : "";
}

// Mesmos limites de internal/escala/precondicoes.go's checarCamposObrigatorios (trava 1) — a fonte da
// verdade é lá, isto só evita a ida ao servidor pra descobrir um ano fora da faixa.
export const ANO_MINIMO_COMPETENCIA = 2000;
export const ANO_MAXIMO_COMPETENCIA = 9999;

// "Setembro/2026"; um mês fora de 1..12 cai no próprio número.
export const rotuloCompetencia = (ano: number, mes: number) => `${MESES[mes - 1] ?? mes}/${ano}`;

// Data e hora (no fuso do navegador) de um timestamp RFC3339.
export function formatDataHoraBR(timestamp?: string) {
  return timestamp ? new Date(timestamp).toLocaleString("pt-BR") : "";
}

function executeEscalaMethod<T>(methodName: string, params: unknown, clientId: string) {
  return apiFetch<T>(`/api/manifest/modules/${MODULE_ID}/methods/${methodName}/execute`, {
    method: "POST",
    body: JSON.stringify(params),
    headers: { "X-Client-ID": clientId },
  });
}

export const listLojas = (clientId: string) =>
  executeEscalaMethod<Loja[]>("listar_lojas", {}, clientId);

// O que um feriado é numa loja: fecha (folga automática), abre (é feriado, mas a loja funciona — o
// dia usa o horário de feriado da carga) ou não é feriado ali (o municipal de outra cidade).
export type EstadoFeriadoLoja = "FECHA" | "ABRE" | "NAO_E_FERIADO";

export const ESTADO_FERIADO_LOJA_LABEL: Record<EstadoFeriadoLoja, string> = {
  FECHA: "Fecha",
  ABRE: "Abre",
  NAO_E_FERIADO: "Não é feriado",
};

// A marcação de uma loja que foge do padrão do feriado.
export interface FeriadoLoja {
  loja_id: string;
  estado: EstadoFeriadoLoja;
}

// Um feriado por data. padrao vale para as lojas sem marcação, inclusive as cadastradas depois;
// lojas traz só as marcações das lojas que o usuário enxerga. Feriado novo nasce ABRE; o criado por
// quem enxerga só algumas lojas vale só nelas. outras_lojas, só leitura: o feriado vale também em
// lojas que o usuário não enxerga, e dele só as lojas próprias mudam (data, nome e ativo, não).
export interface Feriado {
  id?: string;
  data: string;
  nome: string;
  ativo: boolean;
  padrao?: EstadoFeriadoLoja;
  lojas?: FeriadoLoja[];
  outras_lojas?: boolean;
}

export const estadoDoFeriadoNaLoja = (feriado: Feriado, lojaId: string): EstadoFeriadoLoja =>
  feriado.lojas?.find((l) => l.loja_id === lojaId)?.estado ?? feriado.padrao ?? "FECHA";

export const listFeriados = (ano: number, clientId: string) =>
  executeEscalaMethod<Feriado[]>("listar_feriados", { ano }, clientId);

// Rascunho com gente escalada (T) num feriado que fecha a loja: o reflexo não transforma o dia em
// folga; quem monta a escala decide entre corrigir à mão e gerar de novo.
export interface TrabalhoEmFeriadoFechado {
  gerada_id: string;
  escala_nome: string;
  data: string;
  colaboradores: number;
}

// O que salvar um feriado fez nas escalas já geradas: o horário dos dias da data (e da antiga, se
// mudou) refeito nos rascunhos com carga horária, as validadas ou aprovadas que mudariam e os
// rascunhos com gente escalada no feriado que fecha a loja.
export interface ReflexoFeriado extends ReflexoCarga {
  trabalho_em_feriado_fechado?: TrabalhoEmFeriadoFechado[];
}

// Data, nome e ativo; o padrão só vale na criação (sem ele, ABRE). Padrão e lojas mudam depois
// por saveLojasFeriado. Quem enxerga só algumas lojas cria o feriado só nelas, e padrao diz se elas
// fecham ou abrem; se a data já tinha um feriado que ele não via, as lojas dele entram nesse
// (ja_existia), que fica com o nome que tinha.
export const saveFeriado = (feriado: Feriado, clientId: string) =>
  executeEscalaMethod<Feriado & ReflexoFeriado & { ja_existia?: boolean }>("salvar_feriado", feriado, clientId);

// O padrão e as marcações das lojas do usuário: a loja que fica de fora de lojas segue o padrão.
// Lojas que ele não enxerga não mudam. O padrão vale também para elas, então só quem enxerga todas
// as lojas o troca; os demais mandam o que já está.
export const saveLojasFeriado = (
  feriadoId: string,
  padrao: EstadoFeriadoLoja,
  lojas: FeriadoLoja[],
  clientId: string,
) =>
  executeEscalaMethod<Feriado & ReflexoFeriado>(
    "salvar_lojas_feriado",
    { feriado_id: feriadoId, padrao, lojas },
    clientId,
  );

// escala.importar_feriados cria todo feriado abrindo todas as lojas — o mesmo padrão de
// salvar_feriado na criação; importado por quem enxerga só algumas, abre só nelas. As lojas que
// fogem disso são marcadas depois, em "Lojas". Devolve
// também o reflexo no horário dos rascunhos com carga horária e os rascunhos com gente escalada
// num feriado importado que fecha a loja (o que já existia na data).
export const importarFeriados = (linhas: Feriado[], clientId: string) =>
  executeEscalaMethod<ImportarLoteResult & ReflexoFeriado>("importar_feriados", { linhas }, clientId);

// Jornada de um tipo de dia, em "HH:MM". Intervalo opcional, mas em par.
export interface BlocoHorario {
  entrada: string;
  intervalo_inicio?: string;
  intervalo_fim?: string;
  saida: string;
}

// Escala ativa que usa a carga: o tipo e o dia de folga fixa dizem quais blocos ela exige.
export interface EscalaDaCarga {
  id: string;
  nome: string;
  tipo: string;
  dia_folga_fixa?: number | null;
}

// Sábado, domingo e feriado ausentes = bloco em branco: o dia trabalhado nesse tipo de dia fica "?".
// cor é a cor da jornada, "#rrggbb"; ausente = "Padrão". vinculados conta os colaboradores,
// escalas as escalas que escolheram a carga, inclusive excluídas, e dias os dias gerados com ela.
// escalas_ativas só vem para quem enxerga todas as lojas.
export interface CargaHoraria {
  id?: string;
  descricao: string;
  ativa: boolean;
  cor?: string;
  util: BlocoHorario;
  sabado?: BlocoHorario | null;
  domingo?: BlocoHorario | null;
  feriado?: BlocoHorario | null;
  vinculados: number;
  escalas: number;
  dias: number;
  escalas_ativas?: EscalaDaCarga[];
  // Só de saída. travado_por é o nome do sistema externo na carga criada por ele, quando a importação
  // da carga está ligada: aí o dia útil e o sábado só mudam lá.
  origem?: OrigemCadastro;
  travado_por?: string;
}

// Os blocos que o sistema externo informa no horário de um colaborador; o que ele não informa vem ausente.
export interface HorarioIntegracao {
  util: BlocoHorario;
  sabado?: BlocoHorario | null;
  domingo?: BlocoHorario | null;
}

export interface FiltroCargasHorarias {
  descricao?: string;
  ativa?: boolean;
}

export const listCargasHorarias = (clientId: string, filtro: FiltroCargasHorarias = {}) =>
  executeEscalaMethod<CargaHoraria[]>("listar_cargas_horarias", filtro, clientId);

// O que a troca de carga padrão (do colaborador, ou dos horários da carga), ou de um feriado, fez nas
// escalas já geradas: os dias T das em rascunho foram refeitos; as validadas ou aprovadas não mudam e
// vêm contadas.
export interface ReflexoCarga {
  dias_reaplicados?: number;
  escalas_nao_alteradas?: number;
}

// A carga como vai para o servidor: sem o status e as contagens, que só a listagem devolve.
export type CargaHorariaParaSalvar = Omit<CargaHoraria, "ativa" | "vinculados" | "escalas" | "dias" | "escalas_ativas">;

export const saveCargaHoraria = (carga: CargaHorariaParaSalvar, clientId: string) =>
  executeEscalaMethod<CargaHoraria & ReflexoCarga>("salvar_carga_horaria", carga, clientId);

// escala.importar_cargas_horarias — só cria, tudo ou nada; descrição já cadastrada é recusada na linha.
export const importarCargasHorarias = (linhas: CargaHorariaParaSalvar[], clientId: string) =>
  executeEscalaMethod<ImportarLoteResult>("importar_cargas_horarias", { linhas }, clientId);

// Também reativa (ativa: true).
export const ativarCargaHoraria = (id: string, ativa: boolean, clientId: string) =>
  executeEscalaMethod<CargaHoraria>("inativar_carga_horaria", { id, ativa }, clientId);

export const excluirCargaHoraria = (id: string, clientId: string) =>
  executeEscalaMethod<Record<string, never>>("excluir_carga_horaria", { id }, clientId);

// MESES é o catálogo único do seletor de mês — toda tela com campo de
// competência (Prazo, geração) lê daqui, mesmo raciocínio de ESCALA_TIPOS
// acima.
export const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

export interface Prazo {
  id?: string;
  ano: number;
  mes: number;
  inicio_janela: string;
  fim_janela: string;
}

export const listPrazos = (ano: number, clientId: string) =>
  executeEscalaMethod<Prazo[]>("listar_prazos", { ano }, clientId);

export const savePrazo = (prazo: Prazo, clientId: string) =>
  executeEscalaMethod<Prazo>("salvar_prazo", prazo, clientId);

// escala_parametro — uma linha por client, seedada com os defaults abaixo na
// primeira leitura (obter_parametros); não tem "id", edita sempre a linha do
// caller.ClientID (04-contratos.md). max_dias_consecutivos e
// max_domingos_consecutivos são editáveis, mas o Method recusa qualquer
// valor acima do teto legal — ver MAX_DIAS_CONSECUTIVOS_LEGAL/
// MAX_DOMINGOS_CONSECUTIVOS_LEGAL em EscalaParametrosTab.tsx (R02).
export interface Parametros {
  perc_max_folga: number;
  perc_max_folga_domingo: number;
  max_dias_consecutivos: number;
  max_domingos_consecutivos: number;
  dias_minimos_ausencia: number;
  jornada_semanal: number;
}

export const getParametros = (clientId: string) =>
  executeEscalaMethod<Parametros>("obter_parametros", {}, clientId);

export const saveParametros = (parametros: Parametros, clientId: string) =>
  executeEscalaMethod<Parametros>("salvar_parametros", parametros, clientId);

// Lançado aqui ou trazido pela integração externa.
export type OrigemCadastro = "MANUAL" | "INTEGRACAO";

export const ORIGENS_CADASTRO: { value: OrigemCadastro; label: string }[] = [
  { value: "INTEGRACAO", label: "Integração externa" },
  { value: "MANUAL", label: "Manual" },
];

export interface Colaborador {
  id?: string;
  matricula?: string;
  nome: string;
  cpf?: string;
  sexo?: "M" | "F" | "";
  data_nascimento?: string;
  data_admissao: string;
  loja_id: string;
  setor?: string;
  cargo?: string;
  entra_em_escala: boolean;
  ativo: boolean;
  // Carga horária padrão; ausente = sem carga.
  carga_horaria_id?: string | null;
  // Só de saída. travado_por é o nome do sistema externo quando a integração está ativa:
  // aí nome, CPF, sexo, datas, loja, setor, cargo e ativo só mudam lá.
  origem?: OrigemCadastro;
  travado_por?: string;
  // Só de saída: o horário do sistema externo, quando a carga só aceita as equivalentes a ele.
  horario_integracao?: HorarioIntegracao | null;
}

export interface ListarColaboradoresParams {
  loja_id?: string;
  setor?: string;
  cargo?: string;
  nome?: string;
  origem?: OrigemCadastro;
  page: number;
  page_size: number;
}

export interface ListarColaboradoresResult {
  items: Colaborador[];
  total: number;
}

export const listColaboradores = (params: ListarColaboradoresParams, clientId: string) =>
  executeEscalaMethod<ListarColaboradoresResult>("listar_colaboradores", params, clientId);

const TAMANHO_PAGINA_COLABORADORES = 500;

// Traz todos os colaboradores que batem no filtro, paginando até o total: seletores e
// importações precisam do universo inteiro, não de um teto.
export async function listarTodosColaboradores(
  filtros: Omit<ListarColaboradoresParams, "page" | "page_size">,
  clientId: string,
): Promise<Colaborador[]> {
  const pagina = (page: number) =>
    listColaboradores({ ...filtros, page, page_size: TAMANHO_PAGINA_COLABORADORES }, clientId);
  const primeira = await pagina(1);
  const itens = primeira.items ?? [];
  const restantes = Math.ceil((primeira.total - itens.length) / TAMANHO_PAGINA_COLABORADORES);
  if (restantes <= 0) return itens;
  const demais = await Promise.all(Array.from({ length: restantes }, (_, i) => pagina(i + 2)));
  return [...itens, ...demais.flatMap((r) => r.items ?? [])];
}

export const saveColaborador = (colaborador: Colaborador, clientId: string) =>
  executeEscalaMethod<Colaborador & ReflexoCarga>("salvar_colaborador", colaborador, clientId);

// escala.importar_colaboradores — import em lote, tudo-ou-nada: se qualquer
// linha for inválida (aqui ou no banco), nada é gravado e erros lista cada
// linha ruim (linha é 1-based, contando só as linhas enviadas em
// `linhas`). O front resolve loja/situação (nome -> id) antes
// de chamar isso; o Method só valida e insere.
export interface ImportarLinhaErro {
  linha: number;
  erro: string;
  // Na linha repetida, a primeira com o mesmo valor, na mesma contagem de linha.
  linha_anterior?: number;
}

// Formato comum a todo Method escala.importar_* — tudo ou nada, sem meio-termo.
export interface ImportarLoteResult {
  importados: number;
  erros: ImportarLinhaErro[];
}

export type ImportarColaboradoresResult = ImportarLoteResult;

export const importarColaboradores = (linhas: Colaborador[], clientId: string) =>
  executeEscalaMethod<ImportarColaboradoresResult>("importar_colaboradores", { linhas }, clientId);

// Linha da planilha "Vincular cargas horárias", já resolvida pela tela; sem carga remove o vínculo.
export interface VinculoCarga {
  colaborador_id: string;
  carga_horaria_id?: string;
}

// escala.vincular_cargas_horarias — só troca a carga de quem já existe, tudo ou nada. Devolve também
// o reflexo nas escalas já geradas, como salvar_colaborador.
export const vincularCargasHorarias = (linhas: VinculoCarga[], clientId: string) =>
  executeEscalaMethod<ImportarLoteResult & ReflexoCarga>("vincular_cargas_horarias", { linhas }, clientId);

// Quais lojas cada usuário enxerga é vínculo empresa×módulo da plataforma
// (user_company_module, module_id "escala"), gerenciado em /empresas — não
// existe mais Method da Escala pra isso (appdono/CLAUDE.md, "loja da Escala
// é a company da plataforma"). Papel na Escala (module_role) também não
// está neste arquivo: não passa pelo dispatch de Method, ver
// @/api/moduleRole.service.

// escala_ausencia — afastamento, férias e desligamento, discriminados por
// tipo (docs/escala/05-plano.md Fase C). Lançar ou cancelar reescreve os dias
// das escalas em rascunho (dias_afetados/dias_restaurados); as já validadas ou
// aprovadas não mudam e vêm contadas em escalas_nao_alteradas.
export type AusenciaTipo = "AFASTAMENTO" | "FERIAS" | "DESLIGAMENTO";

export const AUSENCIA_TIPOS: { value: AusenciaTipo; label: string }[] = [
  { value: "AFASTAMENTO", label: "Afastamento" },
  { value: "FERIAS", label: "Férias" },
  { value: "DESLIGAMENTO", label: "Desligamento" },
];

export const AusenciaTipoLabels = Object.fromEntries(AUSENCIA_TIPOS.map((t) => [t.value, t.label])) as Record<
  AusenciaTipo,
  string
>;

export interface Ausencia {
  id?: string;
  colaborador_id: string;
  tipo: AusenciaTipo;
  descricao?: string;
  data_inicio: string;
  data_fim?: string;
  cancelada: boolean;
  cancelada_em?: string;
  // Só de saída, como no Colaborador: a ausência travada só se cancela.
  origem?: OrigemCadastro;
  travado_por?: string;
}

export interface ListarAusenciasParams {
  colaborador_id?: string;
  de?: string;
  ate?: string;
  origem?: OrigemCadastro;
}

export interface SalvarAusenciaResult {
  ausencia: Ausencia;
  dias_afetados: number;
  escalas_nao_alteradas: number;
}

export interface CancelarAusenciaResult {
  dias_restaurados: number;
  escalas_nao_alteradas: number;
}

export const listAusencias = (params: ListarAusenciasParams, clientId: string) =>
  executeEscalaMethod<Ausencia[]>("listar_ausencias", params, clientId);

export const saveAusencia = (ausencia: Ausencia, clientId: string) =>
  executeEscalaMethod<SalvarAusenciaResult>("salvar_ausencia", ausencia, clientId);

export const cancelarAusencia = (id: string, clientId: string) =>
  executeEscalaMethod<CancelarAusenciaResult>("cancelar_ausencia", { id }, clientId);

export const importarAusencias = (linhas: Ausencia[], clientId: string) =>
  executeEscalaMethod<ImportarLoteResult>("importar_ausencias", { linhas }, clientId);

// escala_escala — o template de escala (docs/escala/05-plano.md Fase D).
// caracteristica/turno vêm sempre preenchidos do backend (default REGULAR/
// GERAL), então nunca vazios de fato numa Escala já salva; ficam opcionais
// no tipo porque salvar_escala aceita omissão na criação. liberar_fora_prazo
// é só leitura aqui — quem escreve é escala.liberar_fora_prazo.

// ESCALA_TIPOS é o catálogo único das siglas de tipo de escala — toda tela
// que precisa da lista completa (seletor, coluna de tabela, filtro) lê
// daqui, nunca reescreve a sigla ou o rótulo em português por conta própria.
// Pra dar de alta um tipo novo, basta um objeto novo neste array — o tipo
// EscalaTipo, o mapa de rótulos e a lista de opções do formulário são todos
// derivados dele (README.md "Tipos de escala", 03-motor.md). Não existe
// sigla de domingo por sexo: a regra do art. 386 da CLT é aplicada por
// colaborador dentro do próprio gerador 6X1.
//
// Atenção: a sigla também precisa existir em `tiposEscalaValidos`
// (app-dono-product-runtime/internal/escala/template.go) e no mapa de
// geradores do motor — o backend valida a sigla de novo, sem depender deste
// catálogo (são dois runtimes/repos diferentes). Dar de alta um tipo é
// sempre as duas pontas; sigla aceita sem gerador é template que cadastra e
// nunca gera.
export const ESCALA_TIPOS = [
  { value: "6X1", label: "6x1 — folgas espaçadas" },
  { value: "5X2", label: "5x2 — folgas espaçadas" },
  { value: "12X36", label: "12x36" },
  { value: "FOLGA_FIXA", label: "Folga fixa — 1 dia na semana" },
  { value: "FOLGA_FIXA_SAB_DOM", label: "5x2 — sáb e dom" },
  { value: "FOLGA_FIXA_SAB_ALT", label: "Folga fixa — dom e sáb alternado" },
] as const satisfies readonly { value: string; label: string }[];

export type EscalaTipo = (typeof ESCALA_TIPOS)[number]["value"];

export const EscalaTipoLabels = Object.fromEntries(
  ESCALA_TIPOS.map((t) => [t.value, t.label]),
) as Record<EscalaTipo, string>;

export type EscalaCaracteristica = "REGULAR" | "COMPLEMENTAR" | "TRANSFERENCIA";

export const EscalaCaracteristicaLabels: Record<EscalaCaracteristica, string> = {
  REGULAR: "Regular",
  COMPLEMENTAR: "Complementar",
  TRANSFERENCIA: "Transferência",
};

// DIA_SEMANA_LABEL é ISO (segunda = 1) — mesma numeração de dia_folga_fixa
// aqui embaixo e de escala_dia_cheio_fixo.dia_semana (04-contratos.md).
export const DIA_SEMANA_LABEL: Record<number, string> = {
  1: "Segunda",
  2: "Terça",
  3: "Quarta",
  4: "Quinta",
  5: "Sexta",
  6: "Sábado",
  7: "Domingo",
};

export interface Escala {
  id?: string;
  loja_id: string;
  nome: string;
  tipo: EscalaTipo;
  caracteristica?: EscalaCaracteristica | "";
  turno?: string;
  dia_folga_fixa?: number | null;
  liberar_fora_prazo?: boolean;
  liberado_em?: string;
  ativa?: boolean;
  created_by?: string;
  // Com geração viva, loja/tipo/característica/turno/folga fixa/carga horária ficam travados.
  tem_geracao_viva?: boolean;
  // Com carga horária, o dia trabalhado recebe o horário da carga do colaborador, que tem de ser
  // uma destas.
  com_carga_horaria?: boolean;
  cargas_horarias_ids?: string[];
  // Só leitura, preenchido por listar_escalas.
  total_colaboradores?: number;
}

export interface ListarEscalasParams {
  loja_id?: string;
  nome?: string;
  page: number;
  page_size: number;
}

export interface ListarEscalasResult {
  items: Escala[];
  total: number;
}

export const listEscalas = (params: ListarEscalasParams, clientId: string) =>
  executeEscalaMethod<ListarEscalasResult>("listar_escalas", params, clientId);

// Estrategico é uma linha de escala_estrategico: um colaborador dentro de um
// grupo numerado (1 a 5, docs/escala/02-modelo-de-dados.md). nome_grupo é
// redundante entre os membros de um mesmo grupo — cada linha carrega o nome
// de novo porque é assim que a tabela guarda (sem tabela de grupo própria).
export interface Estrategico {
  grupo: number;
  nome_grupo?: string;
  colaborador_id: string;
}

// VinculoTurma é uma linha de escala_escala_colaborador.turma (R30): turma
// ("A" ou "B") do colaborador dentro do vínculo desta escala — só faz
// sentido, e só é exigida, pra escala do tipo FOLGA_FIXA_SAB_ALT. Turma já
// foi campo do cadastro do colaborador; migrou pro vínculo porque um mesmo
// colaborador pode estar em escalas diferentes com necessidades diferentes.
export interface VinculoTurma {
  colaborador_id: string;
  turma: "A" | "B";
}

// EscalaDetalhe é o que obter_escala e salvar_vinculos devolvem
// (docs/escala/04-contratos.md) — a Escala mais a equipe vinculada
// (escala_escala_colaborador, 05-plano.md D2), os grupos estratégicos
// (escala_estrategico, D3), os dias cheios fixos (escala_dia_cheio_fixo,
// D4: linhas de 1 a 6, ISO segunda a sábado — nunca um CSV) e as turmas
// (turmas, R30).
export interface EscalaDetalhe extends Escala {
  colaborador_ids: string[];
  estrategicos: Estrategico[];
  dias_cheios_fixos: number[];
  turmas?: VinculoTurma[];
}

export const obterEscala = (id: string, clientId: string) =>
  executeEscalaMethod<EscalaDetalhe>("obter_escala", { id }, clientId);

export const saveEscala = (escala: Escala, clientId: string) =>
  executeEscalaMethod<Escala>("salvar_escala", escala, clientId);

export const excluirEscala = (id: string, clientId: string) =>
  executeEscalaMethod<Record<string, never>>("excluir_escala", { id }, clientId);

// escala.liberar_fora_prazo é um Method à parte de salvar_escala — papel
// mínimo Gerente (04-contratos.md), acima do Encarregado que o resto do
// formulário exige. Ligar grava liberado_em (quem fica só no backend,
// mesmo padrão de escala_ausencia.cancelada_por); desligar zera os dois.
export const liberarForaPrazo = (escalaId: string, liberar: boolean, clientId: string) =>
  executeEscalaMethod<Escala>("liberar_fora_prazo", { escala_id: escalaId, liberar }, clientId);

// escala.transferir_responsavel passa a escala pra outro usuário (created_by) —
// papel mínimo Administrador de Escala (R34). O erro de "sem papel"/"sem
// acesso à loja" já vem pronto em português.
export const transferirResponsavel = (escalaId: string, novoResponsavelId: string, clientId: string) =>
  executeEscalaMethod<Escala>("transferir_responsavel", { escala_id: escalaId, novo_responsavel_id: novoResponsavelId }, clientId);

// escala.salvar_vinculos substitui a equipe inteira da escala numa chamada
// só — colaborador_ids é a lista final desejada, não um diff
// (docs/escala/05-plano.md D2). A trava de exclusividade (um colaborador só
// numa escala REGULAR ativa por vez) é checada no backend; o erro que ela
// devolve já vem pronto em português, listando nomes e escalas conflitantes.
export interface SalvarVinculosParams {
  escala_id: string;
  colaborador_ids: string[];
  estrategicos: Estrategico[];
  dias_cheios_fixos: number[];
  turmas: VinculoTurma[];
}

export const saveVinculos = (params: SalvarVinculosParams, clientId: string) =>
  executeEscalaMethod<EscalaDetalhe>("salvar_vinculos", params, clientId);

// escala.listar_vinculos_regulares expõe, só leitura, a mesma lista que o
// backend já usa pra trava de exclusividade em salvar_vinculos
// (docs/escala/01-dominio.md "Exclusividade") — client inteiro, não por
// loja. EscalaVinculoModal usa isso pra bloquear no TransferList quem já
// está em outra escala REGULAR/COMPLEMENTAR *antes* de tentar salvar, em vez
// de só mostrar o erro depois do submit.
export interface VinculoRegularAtivo {
  colaborador_id: string;
  escala_id: string;
  escala_nome: string;
}

export const listVinculosRegulares = (clientId: string) =>
  executeEscalaMethod<VinculoRegularAtivo[]>("listar_vinculos_regulares", {}, clientId);

// escala.gerar (docs/escala/05-plano.md F3) — dias_cheios_unicos e
// folgas_compensatorias entram no mesmo payload porque só existem no
// momento da geração de uma competência, sem cadastro próprio
// (01-dominio.md "Dia cheio"/"Folga compensatória").
export interface DiaCheioUnico {
  data: string;
  descricao: string;
}

export interface FolgaCompensatoria {
  colaborador_id: string;
  data_feriado: string;
  data_folga: string;
}

export interface GerarParams {
  escala_id: string;
  ano: number;
  mes: number;
  quantidade_minima: number;
  // Opcionais: em branco, o domingo usa quantidade_minima e a jornada é a dos Parâmetros.
  quantidade_minima_domingo?: number;
  jornada_semanal?: number;
  dias_cheios_unicos: DiaCheioUnico[];
  folgas_compensatorias: FolgaCompensatoria[];
}

export const STATUS_GERADA_LABEL: Record<Gerada["status"], string> = {
  RASCUNHO: "Rascunho",
  VALIDADA: "Validada",
  APROVADA: "Aprovada",
  CANCELADA: "Cancelada",
  REMOVIDA: "Removida",
};

export interface Gerada {
  id: string;
  escala_id: string;
  ano: number;
  mes: number;
  versao: number;
  status: "RASCUNHO" | "VALIDADA" | "APROVADA" | "CANCELADA" | "REMOVIDA";
  quantidade_minima: number;
  quantidade_minima_domingo?: number;
  jornada_semanal?: number;
  gerada_por: string;
  gerada_em?: string;
  validada_por?: string;
  validada_em?: string;
  aprovada_por?: string;
  aprovada_em?: string;
  // Só leitura, preenchido por listar_geradas.
  total_colaboradores?: number;
}

export interface Ocorrencia {
  regra: string;
  colaborador_id: string;
  datas: string[];
}

// Aviso não barra a validação: a tela mostra, e validar e aprovar pedem o aceite da lista.
// DOMINGO_ACIMA_DO_TETO é o domingo com mais gente de folga que o teto do client (folgas e teto);
// PRESENCA_ABAIXO_DO_MINIMO, o dia com menos gente trabalhando que o mínimo da gerada (trabalhando e minimo);
// FOLGAS_SEGUIDAS, no 5X2, as folgas encostadas de um colaborador (colaborador_id e datas; data é a primeira);
// GRUPO_DE_FOLGA_POR_LEI, no 6X1 e no 5X2, os domingos em que o grupo estratégico do colaborador está de folga junto,
// todos por lei (colaborador_id e datas; data é o primeiro);
// HORAS_DA_SEMANA_ACIMA_DA_JORNADA, na escala com carga, a semana de um colaborador com mais horas que a jornada
// (colaborador_id, os sete dias em datas, data é a segunda; minutos trabalhados e jornada_semanal em horas);
// CARGA_SEM_NINGUEM_TRABALHANDO, na escala com carga, os dias em que uma carga tem horário e ninguém trabalha
// com ela (carga_horaria_id e datas; data é o primeiro);
// FOLGA_NAO_CABE_NO_MES e MEDIA_DE_DOMINGO_NAO_CABE, no 6X1 e no 5X2, as contas do teto sobre o mês inteiro: as
// folgas que o mês (ou os domingos, na média) pede contra as que o teto comporta (folgas e teto; data vazia).
export interface Aviso {
  regra: string;
  // Vazia nas contas do mês.
  data: string;
  colaborador_id?: string;
  datas?: string[];
  folgas: number;
  teto: number;
  trabalhando: number;
  minimo: number;
  minutos?: number;
  jornada_semanal?: number;
  carga_horaria_id?: string;
}

// A lista de avisos de um momento da gerada: a que saiu ao gerar e a que o Gerente aceitou ao validar e ao aprovar.
export interface RegistroDeAvisos {
  etapa: "GERADA" | "VALIDADA" | "APROVADA";
  user_id: string;
  em: string;
  avisos: Aviso[];
}

// Impedimento é o "a escala não fecha" de 01-dominio.md's pré-condição 7 —
// nada é persistido quando isto vem preenchido (04-contratos.md: "sem
// gerada, sem nada persistido").
export interface Impedimento {
  regra: string;
  data?: string;
  necessario: number;
  permitido: number;
  // Só em AUSENCIA_NO_GRUPO: fim do trecho, quem fica sem folga e quem do grupo está fora.
  ate?: string;
  colaborador_id?: string;
  ausente_ids?: string[];
}

// Vinculado ativo que ficou de fora de uma escala com carga horária por não ter uma das cargas dela.
export interface ColaboradorSemCarga {
  colaborador_id: string;
  nome: string;
}

export interface GerarResult {
  gerada?: Gerada;
  ocorrencias?: Ocorrencia[];
  avisos?: Aviso[];
  sem_carga?: ColaboradorSemCarga[];
  impedimentos?: Impedimento[];
}

export const gerarEscala = (params: GerarParams, clientId: string) =>
  executeEscalaMethod<GerarResult>("gerar", params, clientId);

// escala.listar_geradas pagina igual listar_escalas — já filtrado pelo
// escopo de loja/autoria do caller e ordenado ano/mês/versão desc. REMOVIDA
// nunca aparece (filtrada no backend); CANCELADA só aparece pra Gerente ou
// acima.
export interface ListarGeradasParams {
  escala_id?: string;
  loja_id?: string;
  nome?: string;
  ano?: number;
  mes?: number;
  status?: Gerada["status"];
  page: number;
  page_size: number;
}

export interface ListarGeradasResult {
  items: Gerada[];
  total: number;
}

export const listGeradas = (params: ListarGeradasParams, clientId: string) =>
  executeEscalaMethod<ListarGeradasResult>("listar_geradas", params, clientId);

// Payload único de escala.obter_espelho: a grade crua, sem totais (os totais
// se calculam no cliente, em components/escala/espelho.ts).
export type TipoDia = "T" | "F" | "C" | "FA" | "A" | "D" | "";

export interface EspelhoGerada {
  id: string;
  escala_nome: string;
  loja_nome: string;
  loja_razao_social?: string;
  loja_cnpj?: string;
  com_carga_horaria?: boolean;
  ano: number;
  mes: number;
  versao: number;
  status: Gerada["status"];
  quantidade_minima: number;
  // Quando a geração informou: o mínimo do domingo e a jornada da escala.
  quantidade_minima_domingo?: number;
  jornada_semanal?: number;
  validada_por?: string;
  validada_em?: string;
  aprovada_por?: string;
  aprovada_em?: string;
}

export interface EspelhoFeriado {
  data: string;
  nome: string;
  fecha_loja: boolean;
}

export interface EspelhoColaborador {
  id: string;
  nome: string;
  matricula?: string;
  // Só os dígitos.
  cpf?: string;
  turma?: string;
  grupo_estrategico?: number;
  // Carga padrão do colaborador hoje.
  carga_horaria_id?: string;
}

// Num T de escala com carga horária vêm a carga e o horário aplicados; sem eles, é o "?". manual
// marca a carga trocada à mão; alterado, o dia que o reset desfaria (tipo mudado ou carga trocada).
export interface EspelhoDia {
  colaborador_id: string;
  data: string;
  tipo: TipoDia;
  carga_horaria_id?: string;
  horario?: BlocoHorario;
  manual?: boolean;
  alterado?: boolean;
}

// Carga que aparece na grade: as da escala e as dos dias, inclusive do histórico. A troca de carga
// no dia oferece as da_escala.
export interface EspelhoCarga {
  id: string;
  descricao: string;
  cor?: string;
  ativa: boolean;
  da_escala?: boolean;
  util: BlocoHorario;
  sabado?: BlocoHorario | null;
  domingo?: BlocoHorario | null;
  feriado?: BlocoHorario | null;
}

// O que o usuário pode fazer agora com a gerada (papel × status): transições de estado e consulta do log, resolvido no backend.
export interface EspelhoAcoes {
  validar: boolean;
  desvalidar: boolean;
  aprovar: boolean;
  desaprovar: boolean;
  log: boolean;
}

export interface Espelho {
  gerada: EspelhoGerada;
  feriados: EspelhoFeriado[];
  colaboradores: EspelhoColaborador[];
  dias: EspelhoDia[];
  historico: EspelhoDia[];
  cargas?: EspelhoCarga[];
  editavel: boolean;
  acoes: EspelhoAcoes;
  // O validador sobre a grade como está; só vem no RASCUNHO.
  ocorrencias?: Ocorrencia[];
  // O que não barra a validação, mas validar e aprovar pedem o aceite; vem no RASCUNHO e na VALIDADA.
  avisos?: Aviso[];
  // As listas gravadas ao gerar, validar e aprovar, da mais antiga para a mais nova.
  historico_avisos?: RegistroDeAvisos[];
}

export const obterEspelho = (geradaId: string, clientId: string) =>
  executeEscalaMethod<Espelho>("obter_espelho", { gerada_id: geradaId }, clientId);

// Uma mudança de estado da gerada, lida da trilha de auditoria. user_id é quem fez; usuario é o
// nome que a trilha guardou, para quando a lista de usuários não tem o autor.
export interface EventoDaGerada {
  evento: "CRIADA" | "VALIDADA" | "DESVALIDADA" | "APROVADA" | "DESAPROVADA" | "CANCELADA" | "REMOVIDA";
  user_id: string;
  usuario?: string;
  em: string;
}

// Da mais antiga para a mais nova.
export const historicoDaGerada = (geradaId: string, clientId: string) =>
  executeEscalaMethod<{ eventos: EventoDaGerada[] }>("historico_gerada", { gerada_id: geradaId }, clientId);

export interface AlterarDiaParams {
  gerada_id: string;
  colaborador_id: string;
  data: string;
  tipo: TipoDia;
}

export interface AlterarDiaResult {
  dia: EspelhoDia;
  ocorrencias: Ocorrencia[];
  avisos?: Aviso[];
}

export const alterarDia = (params: AlterarDiaParams, clientId: string) =>
  executeEscalaMethod<AlterarDiaResult>("alterar_dia", params, clientId);

// Ou a carga do dia, ou padrao para voltar à carga padrão do colaborador.
export interface AlterarCargaDiaParams {
  gerada_id: string;
  colaborador_id: string;
  data: string;
  carga_horaria_id?: string;
  padrao?: boolean;
}

export const alterarCargaDia = (params: AlterarCargaDiaParams, clientId: string) =>
  executeEscalaMethod<AlterarDiaResult>("alterar_carga_dia", params, clientId);

export interface ResetarResult {
  gerada: Gerada;
  ocorrencias: Ocorrencia[];
  avisos?: Aviso[];
  dias_restaurados: number;
}

export const resetarGerada = (geradaId: string, clientId: string) =>
  executeEscalaMethod<ResetarResult>("resetar", { gerada_id: geradaId }, clientId);

// validar=true não muda o estado se houver ocorrência: a lista volta em `ocorrencias`.
// aceite_pendente: a escala tem avisos e o pedido não trouxe o aceite deles, ou trouxe outra lista. O estado
// não mudou, e avisos traz a lista de agora.
export interface ValidarResult {
  gerada: Gerada;
  ocorrencias: Ocorrencia[];
  aceite_pendente?: boolean;
  avisos?: Aviso[];
}

export interface AprovarResult {
  gerada: Gerada;
  aceite_pendente?: boolean;
  avisos?: Aviso[];
}

// avisosAceitos é a lista que a tela mostrou e o Gerente aceitou; o servidor confere com a dele.
export const validarGerada = (geradaId: string, validar: boolean, clientId: string, avisosAceitos?: Aviso[]) =>
  executeEscalaMethod<ValidarResult>("validar", { gerada_id: geradaId, validar, avisos_aceitos: avisosAceitos }, clientId);

// aprovar=false é a desaprovação.
export const aprovarGerada = (geradaId: string, aprovar: boolean, clientId: string, avisosAceitos?: Aviso[]) =>
  executeEscalaMethod<AprovarResult>("aprovar", { gerada_id: geradaId, aprovar, avisos_aceitos: avisosAceitos }, clientId);

// Só em RASCUNHO: apaga a grade e marca CANCELADA (01-dominio.md). Libera a versão pra gerar de
// novo no mesmo mês. A gerada cancelada some das listagens pra quem está abaixo de Gerente.
export const cancelarGerada = (geradaId: string, clientId: string) =>
  executeEscalaMethod<Record<string, never>>("cancelar_gerada", { gerada_id: geradaId }, clientId);

// Só sobre uma CANCELADA, só Gerente ou acima: marca REMOVIDA, que some pra todos de vez.
export const removerGerada = (geradaId: string, clientId: string) =>
  executeEscalaMethod<Record<string, never>>("remover_gerada", { gerada_id: geradaId }, clientId);

// Uma alteração de célula em escala_dia_log. user_id é o autor: o nome vem de /api/modules/escala/users.
// detalhe_de/detalhe_para são o horário de antes e de depois numa escala com carga
// ("Comercial 08:00–18:00" ou "?").
export interface LogEntrada {
  id: string;
  colaborador_id: string;
  colaborador_nome: string;
  data: string;
  de: string;
  para: string;
  motivo: string;
  detalhe_de?: string;
  detalhe_para?: string;
  user_id: string;
  sistema: boolean; // feita pela sincronização automática, sem usuário
  created_at: string;
}

// Filtros opcionais: omitidos quando vazios. de/ate são instantes ISO (ate exclusivo). colaborador
// filtra pelo nome; colaborador_id, pela linha do espelho.
export interface ListarLogParams {
  gerada_id: string;
  colaborador?: string;
  colaborador_id?: string;
  motivo?: string;
  user_id?: string;
  de?: string;
  ate?: string;
  page: number;
  page_size: number;
}

export interface ListarLogResult {
  items: LogEntrada[];
  total: number;
}

export const listarLogGerada = (params: ListarLogParams, clientId: string) =>
  executeEscalaMethod<ListarLogResult>("listar_log", params, clientId);

const MOTIVO_LOG: Record<string, string> = {
  EDICAO_MANUAL: "Edição manual",
  RESET: "Reset",
  AUSENCIA: "Ausência",
  CANCELAMENTO_AUSENCIA: "Cancelamento de ausência",
  TROCA_CARGA: "Troca de carga horária",
  CARGA_PADRAO: "Volta à carga padrão",
  REFLEXO_CARGA: "Reflexo de carga horária",
  REFLEXO_FERIADO: "Reflexo de feriado",
};

export const MOTIVOS_LOG = Object.entries(MOTIVO_LOG).map(([valor, rotulo]) => ({ valor, rotulo }));

// Começo do dia "AAAA-MM-DD" no fuso do navegador, como instante ISO; diasAMais=1 dá o começo do dia
// seguinte, que o servidor trata como limite exclusivo do "até".
export function inicioDoDiaISO(data: string, diasAMais = 0) {
  const [ano, mes, dia] = data.split("-").map(Number);
  return new Date(ano!, mes! - 1, dia! + diasAMais).toISOString();
}

// Rótulo do motivo de um log; um motivo novo cai no próprio código.
export const rotuloDoMotivoLog = (motivo: string) => MOTIVO_LOG[motivo] ?? motivo;

// Autor de uma alteração de célula: o nome vem da lista de usuários do módulo.
export const autorDaAlteracao = (l: { user_id: string; sistema?: boolean }, autores: Map<string, string>) =>
  l.sistema ? "Sincronização automática" : (autores.get(l.user_id) ?? "Usuário não identificado");

// Fase G — relatórios (docs/escala/04-contratos.md, ideia nova de
// 2026-09-28). Todo relatório de competência filtra por ano/mes e só
// considera escalas geradas APROVADA; loja_id/cargo são filtros de tela,
// nenhum deles obrigatório. cargo (texto, substring) não está no contrato original: é o
// filtro que substitui o "NOT LIKE '%APRENDIZ%'" fixo do legado.
export interface RelatorioFiltro {
  ano: number;
  mes: number;
  loja_id?: string;
  cargo?: string;
}

export interface LinhaVT {
  colaborador_id: string;
  matricula?: string;
  nome: string;
  cargo?: string;
  loja_id: string;
  trabalhados: number;
  folgas: number;
  compensacao: number;
  folga_feriado: number;
  afastados: number;
  desligados: number;
}

export const relValeTransporte = (filtro: RelatorioFiltro, clientId: string) =>
  executeEscalaMethod<LinhaVT[]>("rel_vale_transporte", filtro, clientId);

export interface LinhaFolga {
  colaborador_id: string;
  matricula?: string;
  nome: string;
  loja_id: string;
  escala_nome: string;
  data: string;
  tipo: "F" | "FA";
}

export const relFolgas = (filtro: RelatorioFiltro, clientId: string) =>
  executeEscalaMethod<LinhaFolga[]>("rel_folgas", filtro, clientId);

export interface DiaDomingo {
  data: string;
  tipo: TipoDia;
}

// Todo colaborador com domingo no mês, trabalhado ou não; datas são só os trabalhados.
export interface LinhaDomingo {
  colaborador_id: string;
  matricula?: string;
  nome: string;
  sexo?: "M" | "F";
  cargo?: string;
  loja_id: string;
  domingos_trabalhados: number;
  datas: string[];
  domingos: DiaDomingo[];
}

export const relDomingos = (filtro: RelatorioFiltro, clientId: string) =>
  executeEscalaMethod<LinhaDomingo[]>("rel_domingos", filtro, clientId);

export interface SemEscala {
  colaborador_id: string;
  matricula?: string;
  nome: string;
  loja_id: string;
  data_admissao: string;
}

export const relSemEscala = (filtro: RelatorioFiltro, clientId: string) =>
  executeEscalaMethod<SemEscala[]>("rel_sem_escala", filtro, clientId);

// O painel rel_transf do legado era uma cópia do de vale-transporte — não
// serve de especificação. Este relatório é novo: cruza o histórico de
// escala_transferencia com os dias trabalhados em cada uma das duas lojas
// na mesma competência.
export interface LinhaTransferencia {
  id: string;
  colaborador_id: string;
  matricula?: string;
  nome: string;
  loja_anterior_id: string;
  loja_atual_id: string;
  data_transferencia: string;
  dias_loja_anterior: number;
  dias_loja_atual: number;
}

export const relTransferencias = (filtro: RelatorioFiltro, clientId: string) =>
  executeEscalaMethod<LinhaTransferencia[]>("rel_transferencias", filtro, clientId);

// Foto do cadastro atual — sem competência, diferente dos demais.
export interface LinhaColaboradorEscala {
  colaborador_id: string;
  matricula?: string;
  nome: string;
  loja_id: string;
  escala_id?: string;
  escala_nome?: string;
  tipo?: string;
  caracteristica?: string;
  // Competência aprovada mais recente da escala dele: a que a escalinha imprime.
  gerada_aprovada_id?: string;
}

export interface RelColaboradoresPorEscalaFiltro {
  loja_id?: string;
  cargo?: string;
  colaborador?: string;
}

export const relColaboradoresPorEscala = (filtro: RelColaboradoresPorEscalaFiltro, clientId: string) =>
  executeEscalaMethod<LinhaColaboradorEscala[]>("rel_colaboradores_por_escala", filtro, clientId);

// Versão cruzada por loja/competência do escala_dia_log que o modal do
// espelho (listar_log) só mostra por gerada — ficou de fora na Fase F
// (06-correcoes.md) e entra aqui.
export interface LinhaAlteracao {
  id: string;
  colaborador_id: string;
  colaborador_nome: string;
  escala_nome: string;
  loja_id: string;
  data: string;
  de: string;
  para: string;
  motivo: string;
  detalhe_de?: string;
  detalhe_para?: string;
  user_id: string;
  sistema: boolean; // feita pela sincronização automática, sem usuário
  created_at: string;
}

export interface RelAlteracoesParams extends RelatorioFiltro {
  colaborador?: string;
  page: number;
  page_size: number;
}

// Contagens do filtro inteiro, não só da página.
export interface ResumoAlteracoes {
  colaboradores: number;
  edicoes_manuais: number;
  ausencias: number;
  resets: number;
  // Trocas feitas na grade: TROCA_CARGA e CARGA_PADRAO.
  trocas_carga: number;
  // REFLEXO_CARGA: dias refeitos pela mudança da carga padrão ou do horário da carga no cadastro.
  reflexos_carga: number;
  // REFLEXO_FERIADO: dias refeitos por um feriado cadastrado, removido ou mudado de data depois da geração.
  reflexos_feriado: number;
  sistema: number;
}

export interface RelAlteracoesResult {
  items: LinhaAlteracao[];
  total: number;
  resumo: ResumoAlteracoes;
}

export const relAlteracoes = (params: RelAlteracoesParams, clientId: string) =>
  executeEscalaMethod<RelAlteracoesResult>("rel_alteracoes", params, clientId);

// Status da competência por escala (template) ativa — nunca gerada, rascunho,
// validada ou aprovada — cruzado com escala_prazo pra sinalizar atraso.
export interface LinhaAcompanhamento {
  escala_id: string;
  escala_nome: string;
  loja_id: string;
  tipo: string;
  caracteristica: string;
  gerada_id?: string;
  status?: "" | "RASCUNHO" | "VALIDADA" | "APROVADA" | "CANCELADA";
  versao?: number;
  validada_por?: string;
  validada_em?: string;
  aprovada_por?: string;
  aprovada_em?: string;
  prazo_fim_janela?: string;
  atrasada: boolean;
}

export const relAcompanhamento = (filtro: RelatorioFiltro, clientId: string) =>
  executeEscalaMethod<LinhaAcompanhamento[]>("rel_acompanhamento", filtro, clientId);

// Contingente por loja e dia, contra o teto de folga do client
// (PercMaxFolga/PercMaxFolgaDomingo — appdono/CLAUDE.md).
export interface LinhaCobertura {
  loja_id: string;
  data: string;
  trabalhando: number;
  folgando: number;
  contingente: number;
  perc_folga: number;
  teto: number;
  domingo: boolean;
  teto_excedido: boolean;
}

export const relCobertura = (filtro: RelatorioFiltro, clientId: string) =>
  executeEscalaMethod<LinhaCobertura[]>("rel_cobertura", filtro, clientId);

// ---------------------------------------------------------------------------
// Integração externa (07-integracao-senior.md, S9/S9b). A Escala não conhece o
// sistema de origem: provedor, rótulos e campos extras vêm de
// listar_provedores_integracao, e empresa/filial do mapa são textos opacos.
// A senha só trafega daqui para o servidor — nenhum Method a devolve.
// ---------------------------------------------------------------------------

export interface CampoProvedor {
  chave: string;
  rotulo: string;
  obrigatorio: boolean;
}

export interface Provedor {
  id: string;
  nome: string;
  rotulo_empresa: string;
  rotulo_filial: string;
  campos: CampoProvedor[];
  // O sistema sabe informar o horário de cada colaborador (importar_carga_horaria).
  carga_horaria?: boolean;
}

export const listProvedoresIntegracao = (clientId: string) =>
  executeEscalaMethod<Provedor[]>("listar_provedores_integracao", {}, clientId);

export interface Integracao {
  configurada: boolean;
  provedor: string;
  ativa: boolean;
  sincronizar_diariamente: boolean;
  importar_carga_horaria?: boolean;
  url: string;
  usuario: string;
  tem_senha: boolean;
  timeout_seg: number;
  opcoes: Record<string, string>;
}

export const getIntegracao = (clientId: string) =>
  executeEscalaMethod<Integracao>("obter_integracao", {}, clientId);

// senha vazia mantém a gravada; só é obrigatória na primeira configuração e ao
// trocar provedor, URL ou usuário.
export interface SalvarIntegracaoParams {
  provedor: string;
  ativa: boolean;
  sincronizar_diariamente: boolean;
  // Só vale para provedor com carga_horaria; o servidor recusa nos outros.
  importar_carga_horaria: boolean;
  url: string;
  usuario: string;
  senha: string;
  timeout_seg: number;
  opcoes: Record<string, string>;
}

export const saveIntegracao = (params: SalvarIntegracaoParams, clientId: string) =>
  executeEscalaMethod<Integracao>("salvar_integracao", params, clientId);

export interface ResultadoTesteIntegracao {
  ok: boolean;
  mensagem: string;
  filiais: number;
}

// Testa o formulário, sem salvar. Senha em branco usa a gravada, com a mesma regra
// de salvar_integracao (só se provedor, URL e usuário forem os gravados).
export const testarIntegracao = (params: SalvarIntegracaoParams, clientId: string) =>
  executeEscalaMethod<ResultadoTesteIntegracao>("testar_integracao", params, clientId);

export interface MapaLoja {
  loja_id: string;
  empresa: string;
  filial: string;
}

export const listMapaIntegracao = (clientId: string) =>
  executeEscalaMethod<MapaLoja[]>("listar_mapa_integracao", {}, clientId);

// Substitui o mapa inteiro: a lista é o estado desejado.
export const saveMapaIntegracao = (lojas: MapaLoja[], clientId: string) =>
  executeEscalaMethod<MapaLoja[]>("salvar_mapa_integracao", { lojas }, clientId);

export interface SugestaoMapa {
  empresa: string;
  filial: string;
  cnpj: string;
  loja_id?: string;
  loja_nome?: string;
}

export const sugerirMapaIntegracao = (clientId: string) =>
  executeEscalaMethod<SugestaoMapa[]>("sugerir_mapa_integracao", {}, clientId);

export type StatusExecucao = "RODANDO" | "OK" | "PARCIAL" | "ERRO";

export const STATUS_EXECUCAO_LABEL: Record<StatusExecucao, string> = {
  RODANDO: "Em andamento",
  OK: "Concluída",
  PARCIAL: "Concluída com falhas",
  ERRO: "Falhou",
};

export interface ContadoresSincronizacao {
  colaboradores_criados: number;
  colaboradores_atualizados: number;
  colaboradores_desativados: number;
  transferencias: number;
  rejeitados: number;
  ignorados: number;
  ja_cadastrados_manual: number;
  ausencias_criadas: number;
  ausencias_canceladas: number;
  ausencias_reativadas: number;
  ausencias_atualizadas: number;
  feriados_criados: number;
  feriados_atualizados: number;
  cargas_criadas: number;
  colaboradores_carga_alterada: number;
  dias_afetados: number;
  escalas_nao_alteradas: number;
  lojas_sincronizadas: number;
  lojas_com_falha: number;
}

export interface ErroSincronizacao {
  loja_id?: string;
  empresa?: string;
  chave?: string;
  mensagem: string;
}

export interface StatusSincronizacao {
  integracao_ativa: boolean;
  provedor?: string;
  existe: boolean;
  execucao_id?: string;
  status?: StatusExecucao;
  iniciada_em?: string;
  concluida_em?: string;
  contadores?: Partial<ContadoresSincronizacao>;
  erros: ErroSincronizacao[];
}

// Papel mínimo: Gerente.
export const statusSincronizacao = (clientId: string) =>
  executeEscalaMethod<StatusSincronizacao>("status_sincronizacao", {}, clientId);

export const sincronizarIntegracao = (clientId: string) =>
  executeEscalaMethod<{ execucao_id: string; status: StatusExecucao }>("sincronizar_integracao", {}, clientId);

export interface ExecucaoIntegracao {
  id: string;
  status: StatusExecucao;
  iniciada_em: string;
  concluida_em?: string;
  contadores: Partial<ContadoresSincronizacao>;
  // Total de ocorrências; `erros` traz só as primeiras.
  erros_total: number;
  erros: ErroSincronizacao[];
}

// Últimas sincronizações, da mais recente para a mais antiga. Papel mínimo: Gerente.
export const listExecucoesIntegracao = (clientId: string) =>
  executeEscalaMethod<ExecucaoIntegracao[]>("listar_execucoes_integracao", {}, clientId);
